import React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { MessageCircle, Menu, X, Phone, Mail, MapPin, Facebook, Instagram, Monitor, Smartphone, QrCode } from "lucide-react";
import { CONTACT, MESSENGER_URL } from "@/data/staycation";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/landing", label: "Overview" },
  { to: "/units", label: "Available Units" },
  { to: "/rates", label: "Rates" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About the Host" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faqs", label: "FAQs" },
  { to: "/contact", label: "Contact" },
];

const VIEW_STORAGE_KEY = "xs-view-mode";

function useViewMode() {
  const [mode, setMode] = React.useState(() => {
    if (typeof window === "undefined") return "desktop";
    return window.localStorage.getItem(VIEW_STORAGE_KEY) || "desktop";
  });
  React.useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, mode);
    } catch {}
  }, [mode]);
  return [mode, setMode];
}

function ViewModeToggle({ mode, setMode, className = "" }) {
  return (
    <div
      role="group"
      aria-label="View mode"
      data-testid="view-mode-toggle"
      className={`inline-flex items-center rounded-full border border-neutral-200 bg-white p-0.5 ${className}`}
    >
      <button
        type="button"
        onClick={() => setMode("desktop")}
        data-testid="view-mode-desktop"
        aria-pressed={mode === "desktop"}
        title="Desktop view"
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
          mode === "desktop" ? "bg-neutral-900 text-white" : "text-neutral-600 hover:text-neutral-900"
        }`}
      >
        <Monitor className="h-3.5 w-3.5" /> Desktop
      </button>
      <button
        type="button"
        onClick={() => setMode("mobile")}
        data-testid="view-mode-mobile"
        aria-pressed={mode === "mobile"}
        title="Mobile view"
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
          mode === "mobile" ? "bg-neutral-900 text-white" : "text-neutral-600 hover:text-neutral-900"
        }`}
      >
        <Smartphone className="h-3.5 w-3.5" /> Mobile
      </button>
    </div>
  );
}

function QRCodeButton({ className = "" }) {
  const [open, setOpen] = React.useState(false);
  const popoverRef = React.useRef(null);
  const buttonRef = React.useRef(null);
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(siteUrl)}`;

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="qr-code-button"
        aria-expanded={open}
        aria-label="Show QR code"
        title="Scan to open on your phone"
        className={`inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1.5 text-xs font-semibold transition-colors ${
          open ? "border-[var(--xs-primary)] text-[var(--xs-primary)]" : "border-neutral-200 text-neutral-700 hover:text-[var(--xs-primary)] hover:border-[var(--xs-primary)]"
        }`}
      >
        <QrCode className="h-3.5 w-3.5" /> QR
      </button>

      {open && (
        <div
          ref={popoverRef}
          data-testid="qr-code-popover"
          role="dialog"
          aria-label="QR code"
          className="absolute right-0 top-full mt-2 z-[60] w-64 rounded-2xl border border-neutral-200 bg-white shadow-2xl p-4 xs-fade-up"
        >
          <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Open on your phone</p>
          <div className="mt-3 rounded-xl bg-white border border-neutral-100 p-2 flex items-center justify-center">
            <img
              src={qrSrc}
              alt={`QR code linking to ${siteUrl}`}
              width={240}
              height={240}
              data-testid="qr-code-image"
              className="block w-full h-auto"
            />
          </div>
          <p className="mt-3 text-[11px] text-neutral-500 break-all leading-tight">{siteUrl}</p>
          <p className="mt-2 text-[11px] text-neutral-600">Scan with your phone camera to view this site on a real device.</p>
        </div>
      )}
    </div>
  );
}

function Header({ mode, setMode }) {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  React.useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="xs-glass sticky top-0 z-50" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--xs-primary)] text-white font-display font-black">X</span>
            <span className="font-display text-lg font-bold tracking-tight">Xanders<span className="text-[var(--xs-primary)]">.</span>Staycation</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-full transition-colors ${
                    isActive
                      ? "text-[var(--xs-primary)] bg-[var(--xs-primary-soft)]"
                      : "text-neutral-700 hover:text-[var(--xs-primary)] hover:bg-[var(--xs-bg-alt)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <ViewModeToggle mode={mode} setMode={setMode} />
            <QRCodeButton />
            <a
              href={MESSENGER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-messenger-cta"
              className="xs-btn-primary inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"
            >
              <MessageCircle className="h-4 w-4" /> Book via Messenger
            </a>
          </div>

          <button
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-neutral-200"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-5" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-2.5 rounded-xl text-sm font-medium ${
                      isActive
                        ? "text-[var(--xs-primary)] bg-[var(--xs-primary-soft)]"
                        : "text-neutral-800 hover:bg-[var(--xs-bg-alt)]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="px-1 pt-3 flex items-center gap-2">
                <ViewModeToggle mode={mode} setMode={setMode} />
                <QRCodeButton />
              </div>
              <a
                href={MESSENGER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="xs-btn-primary mt-2 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold"
                data-testid="mobile-messenger-cta"
              >
                <MessageCircle className="h-4 w-4" /> Book via Messenger
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="mt-24 border-t border-neutral-200 bg-[var(--xs-bg-alt)]" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--xs-primary)] text-white font-display font-black">X</span>
            <span className="font-display text-lg font-bold">Xanders Staycation</span>
          </Link>
          <p className="mt-4 max-w-md text-sm text-neutral-600 leading-relaxed">
            30 cozy and affordable staycation units near SM Mall of Asia and SM Southmall. Hosted with care by Alexander — because every stay should <em>feel at home</em>.
          </p>
          <a
            href={MESSENGER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="xs-btn-primary mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"
            data-testid="footer-messenger-cta"
          >
            <MessageCircle className="h-4 w-4" /> Chat with us on Messenger
          </a>
        </div>

        <div>
          <p className="xs-eyebrow">Explore</p>
          <ul className="mt-4 space-y-2 text-sm">
            {NAV_LINKS.slice(0, 6).map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-neutral-700 hover:text-[var(--xs-primary)]" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="xs-eyebrow">Contact</p>
          <ul className="mt-4 space-y-3 text-sm text-neutral-700">
            <li className="flex gap-2"><Phone className="h-4 w-4 text-[var(--xs-primary)]" /> +63 995 377 8114 · Alex</li>
            <li className="flex gap-2"><Mail className="h-4 w-4 text-[var(--xs-primary)]" /> {CONTACT.email}</li>
            <li className="flex gap-2"><MapPin className="h-4 w-4 text-[var(--xs-primary)]" /> {CONTACT.address}</li>
            <li className="flex gap-3 pt-1">
              <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook className="h-4 w-4 hover:text-[var(--xs-primary)]" /></a>
              <a href="#" aria-label="Instagram"><Instagram className="h-4 w-4 hover:text-[var(--xs-primary)]" /></a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-neutral-200">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 text-xs text-neutral-500">
          <p>© {new Date().getFullYear()} Xanders Staycation. All rights reserved.</p>
          <p>Hosted by Alexander • Pasay & Las Piñas, Metro Manila</p>
        </div>
      </div>
    </footer>
  );
}

function MessengerFab() {
  return (
    <a
      href={MESSENGER_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="floating-messenger-fab"
      className="xs-float fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-[var(--xs-primary)] hover:bg-[var(--xs-primary-hover)] text-white pl-4 pr-5 py-3 shadow-[0_18px_30px_-12px_rgba(255,56,92,0.6)] font-semibold text-sm transition-colors"
      aria-label="Chat on Messenger"
    >
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/20">
        <MessageCircle className="h-4 w-4" />
      </span>
      Chat on Messenger
    </a>
  );
}

export default function Layout() {
  const [mode, setMode] = useViewMode();
  const isMobile = mode === "mobile";

  return (
    <div
      className={`App min-h-screen flex flex-col ${isMobile ? "bg-neutral-100" : ""}`}
      data-view-mode={mode}
    >
      <Header mode={mode} setMode={setMode} />
      <main className="flex-1">
        {isMobile ? (
          <div className="mx-auto my-6 max-w-[420px] bg-[var(--xs-bg)] border border-neutral-200 rounded-[2rem] shadow-2xl overflow-hidden" data-testid="mobile-frame">
            <Outlet />
          </div>
        ) : (
          <Outlet />
        )}
      </main>
      <Footer />
      <MessengerFab />
    </div>
  );
}
